import React, { useRef, useEffect, useState } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";

import VideoJSPlayer from "./VideoJSPlayer";
import BrowserViewGlobal from "../Views/BrowserView";
import MobileViewGlobal from "../Views/MobileView";
import BackgroundImageView from "./BackgroundImageView";
import BackgroundVideoPlayerBrowserView from "./views/BackgroundVideoPlayer.BrowserView";
import BackgroundVideoPlayerMobileView from "./views/BackgroundVideoPlayer.MobileView";

import {
  isMobileClass,
  isMobileOrTablet,
  isBrowserView,
} from "../Views/MobileClassRenderer";
import videoKinds from "../../data/enums/videoKind";
import useElementHeight from "../hooks/useElementHeight";
import { setCSSVar } from "../../util/colorUtil";

const VideoPlayer = (props) => {
  const {
    backgroundImageLarge,
    backgroundVideoLink,
    backgroundVideoFile,
    navigateToPageOnVideoEnd,
    videoWidth,
    videoTop,
    videoLeft,
    videoEmbed,
    multiLanuageVideoEmbed,
    videoAWS,
    videoKind,
    videoLink,
  } = props;
  const router = useRouter();
  const embedRef = useRef(null);
  const [height] = useElementHeight(embedRef);
  const [showLanguageList, setShowLanguageList] = useState(false);
  const [selectedVideoLanguage, setSelectedVideoLanguage] = useState(null);
  const [selectedMultiLanguageVideo, setSelectedMultiLanguageVideo] =
    useState(null);

  const isLiveVideo =
    videoKind === videoKinds.AWS_LIVESTREAM || videoKind === videoKinds.AWS_VOD;

  const isVideoEmbed = videoEmbed && videoKind === videoKinds.EMBED;

  const multiLanguageVideoEmbedKeys =
    videoKind === videoKinds.MULTI_EMBED &&
    Object.keys(multiLanuageVideoEmbed)
      .map((key) => {
        return { [key]: multiLanuageVideoEmbed[key] };
      })
      .sort((a, b) => {
        const indexA = Object.values(a)[0].index;
        const indexB = Object.values(b)[0].index;
        return indexA - indexB;
      })
      .map((item) => Object.keys(item)[0]);

  const isMultiLanguageVideo =
    multiLanguageVideoEmbedKeys?.length !== 0 &&
    videoKind === videoKinds.MULTI_EMBED;

  const hasAnyVideo = isLiveVideo || isVideoEmbed || isMultiLanguageVideo;

  const isVideoType =
    backgroundVideoFile?.length > 0 ||
    backgroundVideoLink?.length > 0 ||
    hasAnyVideo;

  useEffect(() => {
    if (isMultiLanguageVideo) {
      const firstLanguage = multiLanguageVideoEmbedKeys[0];
      setSelectedVideoLanguage(firstLanguage);
      setSelectedMultiLanguageVideo(multiLanuageVideoEmbed[firstLanguage]);
    }
  }, []);

  useEffect(() => {
    if (typeof height === "number") setCSSVar("video-height", `${height}px`);
  }, [height]);

  const renderVideoPositionByView = (id, renderer) => {
    const isVideoJs = id === "videojs-wrap";
    const paddingTopVideoJs = isVideoJs
      ? { paddingTop: `${((videoWidth ?? 100) / 100) * 56.25}%` }
      : null;
    const paddingTopiFrame =
      videoEmbed || isMultiLanguageVideo
        ? { paddingTop: `${((videoWidth ?? 100) / 100) * 56.25}%` }
        : null;

    return (
      <>
        <BrowserViewGlobal>
          <div
            id={id}
            style={{
              position: "absolute",
              zIndex: 3,
              width: `${videoWidth}%` || "100%",
              top: `${videoTop}%` || "0",
              left: `${videoLeft}%` || "0",
              ...paddingTopVideoJs,
              ...paddingTopiFrame,
            }}
          >
            {renderer()}
            {isMultiLanguageVideo && renderLanguageSelectors()}
          </div>
        </BrowserViewGlobal>
        <MobileViewGlobal>
          <div
            id={id}
            ref={embedRef}
            style={{
              position: "relative",
              marginTop: "0px",
              zIndex: 3,
              width: "100vw",
              height: `${(window.innerWidth / 16) * 9}px`,
            }}
          >
            {renderer()}
            {isMultiLanguageVideo && renderLanguageSelectors()}
          </div>
        </MobileViewGlobal>
      </>
    );
  };

  function renderBackgroundVideo() {
    let source = "";
    if (backgroundVideoFile) {
      source = backgroundVideoFile;
    } else if (backgroundVideoLink) {
      source = backgroundVideoLink;
    }
    if (source) {
      return (
        <>
          {isMobileOrTablet() ? (
            <BackgroundVideoPlayerMobileView
              navigateToPageOnVideoEnd={navigateToPageOnVideoEnd}
              handleOnEnd={handleOnEnd}
              isMobileClass={isMobileClass}
              source={source}
            />
          ) : (
            <BackgroundVideoPlayerBrowserView
              navigateToPageOnVideoEnd={navigateToPageOnVideoEnd}
              handleOnEnd={handleOnEnd}
              isMobileClass={isMobileClass}
              source={source}
            />
          )}
        </>
      );
    } else {
      return <div />;
    }
  }

  function renderVideoEmbed() {
    if (isMultiLanguageVideo) {
      return (
        <>
          <div
            className="video-embed-wrapper"
            dangerouslySetInnerHTML={{
              __html: `${selectedMultiLanguageVideo?.iframe}`,
            }}
          ></div>
        </>
      );
    }
    return (
      <div
        className="video-embed-wrapper"
        dangerouslySetInnerHTML={{
          __html: videoEmbed,
        }}
      ></div>
    );
  }

  function changeSelectedMultiLanguageVideo(value) {
    setSelectedMultiLanguageVideo(multiLanuageVideoEmbed[value]);
    setSelectedVideoLanguage(value);
    setShowLanguageList(false);
  }

  function showLanguages() {
    setShowLanguageList(true);
  }

  function rearrangeArrayBasedOnValue(arr, selectValue) {
    const index = arr.indexOf(selectValue);
    if (index !== -1) {
      arr.splice(index, 1);
      arr.unshift(selectValue);
    }
    return arr;
  }

  function renderLanguageSelectors() {
    return (
      <div
        className={`video-multi-language-embed-wrapper ${
          showLanguageList ? "multi-lang" : "single-lang"
        }`}
      >
        {rearrangeArrayBasedOnValue(
          multiLanguageVideoEmbedKeys,
          selectedVideoLanguage
        )?.map((value) => {
          if (multiLanuageVideoEmbed.hasOwnProperty(value)) {
            if (!showLanguageList) {
              return (
                multiLanuageVideoEmbed[value] ===
                  selectedMultiLanguageVideo && (
                  <div
                    id={value}
                    className={`video-multi-language-embed-video-language lang-${value} single-lang`}
                    onClick={() => showLanguages(value)}
                  >
                    {multiLanuageVideoEmbed[value].text}
                  </div>
                )
              );
            } else {
              return (
                <div
                  id={value}
                  className={`video-multi-language-embed-video-language lang-${value} multi-lang ${
                    selectedVideoLanguage !== value ? "lang-transparent" : ""
                  }`}
                  onClick={() => changeSelectedMultiLanguageVideo(value)}
                >
                  {multiLanuageVideoEmbed[value].text}
                </div>
              );
            }
          }
        })}
      </div>
    );
  }

  function renderLivePlayer() {
    return <VideoJSPlayer src={videoLink} />;
  }

  function handleOnEnd(e) {
    if (navigateToPageOnVideoEnd) {
      router.push("/[event]/[page]", navigateToPageOnVideoEnd.getFrontendUrl);
    }
  }

  return (
    <>
      {isMobileOrTablet() && !hasAnyVideo && renderBackgroundVideo()}
      {isBrowserView() && renderBackgroundVideo()}

      {(videoEmbed || isMultiLanguageVideo) &&
        renderVideoPositionByView(
          `${isMobileOrTablet() ? "mobile-" : ""}video-embed-wrap`,
          renderVideoEmbed
        )}

      {isLiveVideo && (
        <>{renderVideoPositionByView("videojs-wrap", renderLivePlayer)}</>
      )}

      <BackgroundImageView
        backgroundImage={backgroundImageLarge}
        isVideoType={isVideoType}
      />
    </>
  );
};

export default dynamic(() => Promise.resolve(React.memo(VideoPlayer)), {
  ssr: false,
});
